/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getVehicleHistory} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface VehicleHistoryParams {
  vinId: string;
  startDate?: string | null;
  endDate?: string | null;
  staleTime?: DurationResult | number;
}

export type UseVehicleHistoryResult = UseDefaultQueryResult & {
  vehicleHistory: GenericObject[];
  refetchHistory: () => void;
  refresh: () => void;
};

/**
 * Retrieves the vehicle history for the given VIN / ESN / Mobile ID.
 *
 * ```typescript
 *
 *   import {useLoadVehicleHistory} from '@ion/query/src/hooks/';
 *
 *   ...
 *
 *   const {vehicleHistory, refetchHistory, refresh} = useLoadVehicleHistory({
 *     vinId,
 *     startDate,
 *     endDate
 *   });
 *
 * ```
 *
 * @param vinId     VIN identifier for the vehicle
 * @param startDate Optional start date for filtering history
 * @param endDate   Optional end date for filtering history
 * @param staleTime How long the query should hold onto details
 *                  before re-fetching
 * 
 * @returns Object containing vehicleHistory array, refetchHistory function, and refresh function
 */
export const useLoadVehicleHistory = ({
  vinId,
  startDate,
  endDate,
  staleTime = getStaleTime(0, false, false)
}: VehicleHistoryParams): UseVehicleHistoryResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.LOAD_VEHICLE_HISTORY, {
      vinId,
      startDate,
      endDate
    })
  ), [vinId, startDate, endDate]);

  const query = useQuery(key, async() => {
    try {
      const results = await getVehicleHistory({
        vinId,
        startDate,
        endDate
      });
      return results?.data?.response?.results;
    } catch (err) {
      error(err);
      return [];
    }
  }, {
    staleTime : staleTime as number,
    enabled   : !!vinId,
    retry     : 0
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [queryClient, key]);

  const refetchHistory = useCallback(() => {
    query.refetch();
  }, [query]);

  return createQueryResult(query, {
    vehicleHistory : query.data || [],
    refetchHistory,
    refresh
  }) as UseVehicleHistoryResult;
};

export default useLoadVehicleHistory;
